import { useCallback, useMemo, useSyncExternalStore } from 'react';

/**
 * Subscribe to a media query.
 *
 * `useSyncExternalStore` rather than `useState` + `useEffect`: the query is external state that can
 * change without React knowing, and reading it in an effect means one render at the wrong value
 * first. That matters here because this hook decides **which markup exists**, not just how it
 * looks — see `useIsWide` below.
 *
 * There is no server snapshot. This app is client-rendered only; adding a fabricated one would let
 * a future SSR pass hydrate against a guess instead of failing loudly.
 *
 * **One `MediaQueryList`, memoised on `query`, shared by `subscribe` and `getSnapshot`.**
 * `useSyncExternalStore` calls `getSnapshot` on every render (and again on every store change, to
 * check for tearing) — every table on the page calls this hook for its own breakpoint, so
 * `getSnapshot` is a hot path. The original version called `window.matchMedia(query)` fresh inside
 * `getSnapshot` itself, allocating and re-parsing the query string on every single render for no
 * reason; `subscribe` already builds the same `MediaQueryList` for its own listener, so this reuses
 * that one instance instead of a second, throwaway one.
 */
export function useMediaQuery(query: string): boolean {
  const list = useMemo(() => window.matchMedia(query), [query]);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      list.addEventListener('change', onStoreChange);
      return () => {
        list.removeEventListener('change', onStoreChange);
      };
    },
    [list],
  );

  const getSnapshot = useCallback(() => list.matches, [list]);

  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * Is the viewport wide enough for the desktop navigation rail?
 *
 * 64rem, matching Tailwind's own `lg`, chosen because the rail is 15rem and the content column
 * needs at least 45rem before the two together stop being an improvement over stacking.
 */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 64rem)');
}

/**
 * Padding around a table-or-cards panel at the breakpoint where the table takes over — the page's
 * own outer padding plus `Panel`'s own horizontal padding, which is **not one constant**:
 * `primitives.tsx`'s `Panel` is `p-4 sm:p-6`, 1rem a side below Tailwind's `sm` breakpoint (640px /
 * 40rem) and 1.5rem a side at or above it. `AccessPage.tsx`'s table breaks at 36rem, comfortably
 * below 40rem, so `p-4` (2rem total) was always the right constant *for that one call site* — but it
 * is not a fact about tables in general, only about a breakpoint that happens to land under 40rem.
 *
 * **Found as a real, measured bug, not a hypothetical**: `platform/PlatformPage.tsx`'s two tables
 * declare `min-width: 40rem` and `44rem`. Both breakpoints land *at or above* 40rem, where `Panel`
 * is already in its `sm:p-6` regime — so the flat 2rem constant undercounted the real padding by a
 * full 1rem, and a real, narrow horizontal-overflow band existed at the computed breakpoints (736px–
 * 744px for the 44rem table, measured directly in a real browser, confirmed to close by 748px).
 * `PADDING_BELOW_SM_REM 40` is the same reasoning: whichever total actually applies depends on
 * whether the *resulting breakpoint* clears 40rem, which is why this is a function of the table's own
 * width rather than a second bare constant.
 */
const PADDING_BELOW_SM_REM = 2;
const PADDING_AT_OR_ABOVE_SM_REM = 3;
const SM_BREAKPOINT_REM = 40;

/**
 * The first viewport width, in rem, at which a table declaring `tableMinWidthRem` fits without a
 * sideways scroll. Pulled out as a pure function, separate from the hook that reads it, so it has a
 * test that needs no DOM (`useMediaQuery.test.ts`) — this repo's test runner is plain `node --test`,
 * with no jsdom.
 *
 * Try the lighter padding first; if the resulting breakpoint would still be under 40rem, `Panel` is
 * genuinely in its `p-4` regime there and the lighter total is correct (this is `AccessPage.tsx`'s
 * own case: 34 + 2 = 36, under 40). Otherwise the breakpoint lands at or above 40rem, where `Panel`'s
 * `sm:p-6` is already in effect, and the heavier total is the one that is actually true at that
 * width — `platform/PlatformPage.tsx`'s two tables are both this case.
 */
export function tableBreakpointRem(tableMinWidthRem: number): number {
  const lightBreakpoint = tableMinWidthRem + PADDING_BELOW_SM_REM;
  return lightBreakpoint < SM_BREAKPOINT_REM
    ? lightBreakpoint
    : tableMinWidthRem + PADDING_AT_OR_ABOVE_SM_REM;
}

/**
 * Is there room for a table whose own CSS declares `min-width: {tableMinWidthRem}rem`?
 *
 * **Derived from the table's own declared minimum width, not guessed, and not shared across
 * different tables with different column counts.** `docs/comparison-emrx.md` §7 records picking one
 * breakpoint from a guess about what "a table" in general needs as one of the traps worth not
 * repeating; a second table with a different column count and a different declared `min-width` needs
 * its *own* breakpoint computed the same way, not a reuse of another table's number — the exact gap
 * `platform/PlatformPage.tsx`'s two tables fell into by both defaulting to this hook's original
 * single hard-coded value: a viewport between that shared value and their real minimums reported
 * "room for a table" when there genuinely was not, producing horizontal overflow with no scroll
 * wrapper. Caught by independent review, not by this project's own phone/desktop verification sweep,
 * which happened not to land inside that particular gap.
 *
 * Below the breakpoint the page mounts cards *instead of* the table, never both — `BACKLOG.md` B10.
 * A CSS-toggled dual DOM (both rendered, one hidden) puts two copies of every row in the document
 * at once, producing duplicate `id`s (a `<label for>` binding to the hidden copy, leaving the
 * visible control with no accessible name) and Playwright strict-mode failures.
 */
export function useHasRoomForTable(tableMinWidthRem = 34): boolean {
  return useMediaQuery(`(min-width: ${String(tableBreakpointRem(tableMinWidthRem))}rem)`);
}

/** The OS colour preference. Only meaningful when the user's own preference is `'system'`. */
export function usePrefersDark(): boolean {
  return useMediaQuery('(prefers-color-scheme: dark)');
}

/**
 * Is there room for the schedule view's appointment table?
 *
 * **Measured in a real browser, not computed by hand** — an independent review of this hook's first
 * version (`46rem`, derived on paper from an assumed `2rem` of surrounding page padding) found that
 * assumption wrong: `Panel`'s padding is `p-4` below Tailwind's `sm` breakpoint (40rem) but `sm:p-6`
 * above it, and 46rem is itself above 40rem, so the real page was already past that crossover at the
 * chosen threshold — the table mounted and genuinely overflowed in the ~736–784px range, pushing the
 * Actions column (Reschedule/status buttons) out of view. Recomputing the padding by hand a second
 * time risks the identical mistake, so this number instead comes from a live width sweep against the
 * real running page (`AppointmentList`'s `DesktopTable`, `min-w-[44rem]`, with both its variable-width
 * columns — Patient and Type — populated with realistic long content): `document.documentElement.
 * scrollWidth > window.innerWidth` (genuine page-level horizontal overflow, the actual user-visible
 * symptom) flips from `true` to `false` between 744px and 745px. `47rem` (752px) is that measured
 * crossover rounded up to a clean value, not a formula. Below it the page mounts cards *instead of*
 * the table, matching `useHasRoomForTable`'s own reasoning against a dual DOM.
 */
export function useHasRoomForScheduleTable(): boolean {
  return useMediaQuery('(min-width: 47rem)');
}

/**
 * Is the viewport wide enough for `CalendarGrid`'s real time-grid layout, rather than the single-day
 * agenda (`AppointmentList`) `SchedulePage` falls back to below this breakpoint?
 *
 * **Deliberately not derived the way `useHasRoomForTable`/`useHasRoomForScheduleTable` are** — those
 * two exist to answer "does the table genuinely overflow the page below this width," which the grid
 * does not have the same version of: `CalendarGrid` already wraps itself in its own
 * `overflow-x-auto`, so it never pushes the page itself into horizontal overflow at any width, even a
 * phone's — a many-column "all providers" day view simply gains its own internal scrollbar. This
 * breakpoint answers a different, genuinely subjective question instead: below what width does a
 * multi-column time grid stop being a *better* way to see a schedule than a single day's agenda list,
 * the same choice every mainstream calendar app makes between a week/day grid and a list view on a
 * phone. 48rem (768px, Tailwind's own `md`) is chosen as a conventional tablet-and-up cutoff for that
 * UX judgement, not measured against any one table's declared minimum width — confirmed live (not
 * just asserted) at both a genuine phone width and a genuine desktop width, per this project's own
 * mobile-first/real-browser verification discipline, that neither the grid nor the agenda fallback
 * produces page-level horizontal overflow at its own side of this breakpoint.
 */
export function useHasRoomForCalendarGrid(): boolean {
  return useMediaQuery('(min-width: 48rem)');
}

/**
 * Is there room for the inbox's two-pane layout (`routes/InboxPage.tsx`) — a filterable thread list
 * alongside an open thread's messages and compose box — rather than the stacked, one-pane-at-a-time
 * fallback below this breakpoint?
 *
 * Below this breakpoint, `InboxPage.tsx` mounts the thread list *or* the open thread, never both at
 * once — matching `useHasRoomForTable`'s own reasoning against a CSS-toggled dual DOM — so unlike
 * `useHasRoomForScheduleTable`'s own table (which stays mounted and can genuinely overflow), the
 * split layout here is never actually on screen below this width for `scrollWidth` to catch failing.
 * The number itself is still verified live rather than left as a paper guess: a real open thread
 * with a genuinely long, unbroken subject and message body (no natural wrap point for the browser to
 * fall back on) was rendered in a disposable headless Chrome instance against `AppShell`'s own
 * desktop nav rail (`useIsDesktop`'s 64rem, 15rem wide) at both 1030px (just below this breakpoint —
 * confirmed stacked, single-pane, no overflow) and 1040px (`65rem`, confirmed split, no overflow) —
 * `document.documentElement.scrollWidth === window.innerWidth` held at both. `65rem` is comfortable
 * headroom above `useIsDesktop`'s own 64rem nav-rail breakpoint, not a value picked to sit exactly on
 * an overflow edge.
 */
export function useHasRoomForInboxSplitView(): boolean {
  return useMediaQuery('(min-width: 65rem)');
}
